import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { environment } from '../../../../../environments/environment';
import { lastValueFrom, take } from 'rxjs';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import { AuthService } from '../../../auth/services/auth.service';

const DRAFT_STATUS = ['DRAFT', 'READY_FOR_VIDEO_GENERATION'];
/** Shared with features/content ContentService.invalidateQueries after video generation. */

@Injectable({ providedIn: 'root' })
export class AdminContentService {
  #http = inject(HttpClient);
  #authService = inject(AuthService);

  hasDrafts = signal(false);
  hasNotPublishedCourses = signal(false);
  hasGeneratingVideos = signal(false);

  /**
   * Set true while the admin content view needs GET /courses/valvur (ADMIN).
   */
  isAvailableCoursesQueryEnabled = signal(false);
  availableCourses = computed(() => this.valvurCoursesQuery.data()?.data ?? []);

  readonly valvurCoursesQuery = injectQuery(() => ({
    queryKey: ['valvurCourses'],
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<ChallengeGen.CourseRes>(`${environment.baseUrl}/courses/valvur`)
          .pipe(take(1)),
      ),
    select: (data: ChallengeGen.CourseRes) => {
      this.hasDrafts.set(
        Array.isArray(data.data) &&
          data.data.some((course) => course.status === 'DRAFT'),
      );
      this.hasNotPublishedCourses.set(
        Array.isArray(data.data) &&
          data.data.some((course) => DRAFT_STATUS.includes(course.status)),
      );
      return data;
    },
    enabled:
      this.#authService.isAuthenticated() &&
      this.isAvailableCoursesQueryEnabled(),
    refetchInterval: (): number | false => {
      const shouldPoll =
        this.isAvailableCoursesQueryEnabled() &&
        (this.hasDrafts() || this.hasGeneratingVideos());
      return shouldPoll ? 60000 : false;
    },
  }));
}
